import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { breadcrumbSchema } from '../../data/schema';
import '../../css/BeforeAfter.css';
import '../../css/BeforeAfterGallery.css';
import Footer from '../footer/Footer';
import Header from '../Header';

const MONTHS = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// Chaque image regroupe déjà le "avant" (moitié gauche) et le "après" (moitié droite)
const storages = [
    { image: '/img/before-after/2026_09_deauville/before_after_2026_september_deauville_showcase.png', date: '09/2026', city: 'Deauville', title: 'Résidence Carol Parc', url: 'deauville-septembre-2026' },
    { image: '/img/before-after/2026_09_blonville-sur-mer/before_after_2026_september_blonville_showcase.png', date: '09/2026', city: 'Blonville-sur-Mer', url: 'blonville-septembre-2026' },
    { image: '/img/before-after/2026_09_trouville-sur-mer/before_after_2026_september_trouville_showcase.png', date: '09/2026', city: 'Trouville-sur-Mer', url: 'trouville-septembre-2026' },
    { image: '/img/before-after/2026_08_villers-sur-mer/before_after_2026_august_villers_showcase.png', date: '08/2026', city: 'Villers-sur-Mer', url: 'villers-aout-2026' },
    { image: '/img/before-after/2026_05_deauville/before_after_2026_may_deauville_showcase.png', date: '05/2026', city: 'Deauville', title: 'Rue Jean Mermoz', url: 'deauville-mai-2026' },
    { image: '/img/before-after/2026_02_deauville/before_after_2026_february_deauville_showcase.png', date: '02/2026', city: 'Deauville', title: 'Rue du stade', url: 'deauville-fevrier-2026' },
    { image: '/img/before-after/2026_01_deauville/before_after_2026_january_deauville_showcase.png', date: '01/2026', city: 'Deauville', title: 'Quai de la Touques', url: 'deauville-janvier-2026' },
    { image: '/img/before-after/2026_01_trouville-sur-mer/before_after_2026_january_trouville_showcase.png', date: '01/2026', city: 'Trouville-sur-Mer', title: 'Quai Albert 1er', url: 'trouville-janvier-2026' },
    { image: '/img/before-after/2025_10_deauville/before_after_2025_october_deauville_showcase.png', date: '10/2025', city: 'Deauville', title: 'Les Marinas', url: 'deauville-octobre-2025' },
    { image: '/img/before-after/2025_04_tourgeville/before_after_2025_april_tourgeville_showcase.png', date: '04/2025', city: 'Tourgéville', title: 'Boulevard de la Mer', url: 'tourgeville-avril-2025' },
    { image: 'before_after_2025_march_trouville.png', date: '03/2025', city: 'Trouville-sur-Mer', title: 'Avenue du Parc Cordier' },
    { image: 'before_after_2025_january_villers.png', date: '01/2025', city: 'Villers-sur-Mer', title: 'Résidences Les Marines' },
    { image: '/img/before-after/2025_01_touques/before_after_2025_january_touques_showcase.png', date: '01/2025', city: 'Touques', title: 'Chemin de la Coquetière', url: 'touques-janvier-2025' },
    { image: '/img/before-after/2024_09_deauville/before_after_2024_september_deauville_showcase.png', date: '09/2024', city: 'Deauville', title: 'Résidence Le Fairway', url: 'deauville-septembre-2024' },
];

// Nom de fichier seul : image dans /img/home. Chemin complet : image vitrine du dossier du débarras
const imageSrc = (image) => (image.startsWith('/') ? image : `/img/home/${image}`);

const formatDate = (dateString) => {
    const [month, year] = dateString.split('/');
    return `${MONTHS[parseInt(month, 10) - 1]} ${year}`;
};

const ALL = 'Toutes';

const BeforeAfterCard = ({ storage, onOpen }) => (
    <article className="ba-card">
        <button type="button" className="ba-card-media" onClick={onOpen} aria-label={`Agrandir le débarras ${formatDate(storage.date)}, ${storage.city}`}>
            <img
                src={imageSrc(storage.image)}
                alt={`Avant / après débarras ${formatDate(storage.date).toLowerCase()} ${storage.city}`}
                loading="lazy"
            />
            <span className="ba-tag ba-tag--before">Avant</span>
            <span className="ba-tag ba-tag--after">Après</span>
        </button>
        <div className="ba-card-body">
            <div>
                <p className="ba-card-meta">{formatDate(storage.date)}</p>
                <h2 className="ba-card-title">{storage.city }</h2>
                {storage.title && <p className="ba-card-city">{storage.title}</p>}
            </div>
            {storage.url && (
                <a href={`/avant-apres/${storage.url}`} className="ba-card-link">
                    Plus de photos
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M7 17L17 7M17 7H7M17 7V17" />
                    </svg>
                </a>
            )}
        </div>
    </article>
);

const BeforeAfter = () => {
    const [city, setCity] = useState(ALL);
    const [openIndex, setOpenIndex] = useState(null);

    const cities = useMemo(() => {
        const counts = storages.reduce((acc, s) => ({ ...acc, [s.city]: (acc[s.city] || 0) + 1 }), {});
        return Object.keys(counts).sort((a, b) => counts[b] - counts[a] || a.localeCompare(b));
    }, []);

    const filtered = useMemo(
        () => (city === ALL ? storages : storages.filter((s) => s.city === city)),
        [city]
    );

    const close = useCallback(() => setOpenIndex(null), []);
    const next = useCallback(() => setOpenIndex((i) => (i + 1) % filtered.length), [filtered.length]);
    const prev = useCallback(() => setOpenIndex((i) => (i - 1 + filtered.length) % filtered.length), [filtered.length]);

    useEffect(() => {
        if (openIndex === null) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') close();
            if (e.key === 'ArrowRight') next();
            if (e.key === 'ArrowLeft') prev();
        };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [openIndex, close, next, prev]);

    const current = openIndex !== null ? filtered[openIndex] : null;

    return (
        <>
            <Helmet>
                <title>Avant / après : nos débarras en photos | CC Débarras 14</title>
                <meta name="description" content="Photos avant/après de nos débarras de maisons et appartements à Deauville, Trouville-sur-Mer, Touques, Villers-sur-Mer, Blonville-sur-Mer et sur la Côte Fleurie." />
                <link rel="canonical" href="https://ccdebarras14.fr/avant-apres" />
                <meta property="og:title" content="Avant / après : nos débarras en photos | CC Débarras 14" />
                <meta property="og:description" content="Photos avant/après de nos débarras de maisons et appartements à Deauville, Trouville-sur-Mer, Touques, Villers-sur-Mer, Blonville-sur-Mer et sur la Côte Fleurie." />
                <meta property="og:url" content="https://ccdebarras14.fr/avant-apres" />
                <meta property="og:type" content="website" />
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema([
                    { name: 'Accueil', path: '/' },
                    { name: 'Avant / Après', path: '/avant-apres' },
                ]))}</script>
            </Helmet>

            <Header />

            <div className="before-after-component ba-page">
                <div className="before-after-content">
                    <header className="ba-hero">
                        <h1>Avant / Après</h1>
                        <div className="ba-hero-stats">
                            <div><strong>{storages.length}</strong><span>chantiers en images</span></div>
                            <div><strong>{cities.length}</strong><span>communes</span></div>
                        </div>
                    </header>

                    <nav className="ba-filters" aria-label="Filtrer par commune">
                        {[ALL, ...cities].map((c) => (
                            <button
                                key={c}
                                type="button"
                                className={`ba-chip${city === c ? ' is-active' : ''}`}
                                onClick={() => setCity(c)}
                                aria-pressed={city === c}
                            >
                                {c}
                            </button>
                        ))}
                    </nav>

                    <div className="ba-grid">
                        {filtered.map((storage, idx) => (
                            <BeforeAfterCard key={storage.image} storage={storage} onOpen={() => setOpenIndex(idx)} />
                        ))}
                    </div>

                    <section className="ba-cta">
                        <h2>Un logement à vider&nbsp;?</h2>
                        <p>Visite et devis gratuits, débarras gratuit sous conditions.</p>
                        <a href="/contact" className="ba-cta-button">Demander un devis</a>
                    </section>
                </div>
            </div>

            {current && (
                <div className="ba-lightbox" role="dialog" aria-modal="true" aria-label="Photo avant / après" onClick={close}>
                    <button type="button" className="ba-lightbox-close" onClick={close} aria-label="Fermer">×</button>
                    {filtered.length > 1 && (
                        <button type="button" className="ba-lightbox-nav ba-lightbox-nav--prev" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Précédent">‹</button>
                    )}
                    <figure className="ba-lightbox-figure" onClick={(e) => e.stopPropagation()}>
                        <div className="ba-lightbox-media">
                            <img src={imageSrc(current.image)} alt={`Avant / après débarras ${formatDate(current.date).toLowerCase()} ${current.city}`} />
                            <span className="ba-tag ba-tag--before">Avant</span>
                            <span className="ba-tag ba-tag--after">Après</span>
                        </div>
                        <figcaption>
                            <span>{current.title ? `${current.title} · ` : ''}{current.city} — {formatDate(current.date)}</span>
                            <span className="ba-lightbox-count">{openIndex + 1} / {filtered.length}</span>
                        </figcaption>
                    </figure>
                    {filtered.length > 1 && (
                        <button type="button" className="ba-lightbox-nav ba-lightbox-nav--next" onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Suivant">›</button>
                    )}
                </div>
            )}

            <Footer />
        </>
    );
};

export default BeforeAfter;
